import { playSample } from './audio'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX, JUMP_SFX } from './missionSfx'
import { createTemplateCloneRestorer } from './primitives/restoreClonedTemplates'
import { createDeathWatcher } from './primitives/watchAvatarDeath'
import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
} from './types'

/**
 * RJS08（ラウンドせい: はんてい と せいこう）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS08.rbxl）のギミックを移植する:
 * - ServerScriptService/GameManager:
 *   - BindableEvent「せいこう」受信で Round（IntValue、初期値 1）を +1 し、
 *     Winner を鳴らす。
 *   - BindableEvent「はんてい」受信で Telling the Judge - Sting 1 を鳴らし、
 *     CountEvent で全クライアントへカウントダウンを指示する。
 * - StarterGui/GUILocalScript:
 *   - カウントダウン: CountTextLabel を表示し 3 → 2 → 1 を 1 秒ずつ出して隠す。
 *     isCount デバウンスにより 1 ラウンドにつき一度だけ動く（せいこう の表示が
 *     終わるまで再カウントしない）。
 *   - Round.Changed: RoundTextLabel を更新し、SuccessTextLabel（せいこう！）を
 *     3 秒表示して隠し、isCount を解除する。
 *   - 初期表示: RoundTextLabel に Round の初期値（1）。
 * - ServerScriptService/PlayerDeathHandler: 死亡（Humanoid.Died）のたびに
 *   Game Over を鳴らす。
 * - ServerStorage の ②ブロック: 学習者プログラム用のテンプレート（配下の
 *   Script は空 = 学習者枠）。実機では ServerStorage に居て見えない。
 *
 * プレビューでの近似・意図的な差異:
 * - 「せいこう」「はんてい」は学習者プログラムのメッセージ送信（実機の
 *   BindableEvent:Fire）で届くため onMessage に紐づける。
 * - GUI は 3 枚の TextLabel（Round / Count / Success）を HUD テキスト
 *   1 チャンネルで近似する。実機の重なり順（Success が Count より手前、
 *   どちらも常時表示の Round 帯とは別位置）に合わせ、
 *   せいこう！ > カウントダウン > ラウンド数 の優先で表示する。
 * - Winner / Game Over / Telling the Judge 音（実機の音源はアセット取得不可）は
 *   プロダクト提供の実音源で鳴らす（せいこう=成功音、死亡=失敗音、
 *   はんてい=合図音）。
 * - 死亡検知は Humanoid.Died の代わりに体力 0 のエッジ（生存 → 0）の
 *   毎フレーム監視で行う（リスポーン後の再死亡でも毎回鳴る実機挙動を再現）。
 *
 * 未対応: なし（Tool・装備系のギミックはこのミッションに存在しない）。
 */

/** 学習者プログラム用テンプレート（実機では ServerStorage）のオブジェクト名。 */
const TEMPLATE_NAMES: ReadonlyArray<string> = ['②ブロック']

/** 学習者プログラムが送るメッセージ名（ServerStorage の BindableEvent 名）。 */
const SUCCESS_MESSAGE = 'せいこう'
const JUDGE_MESSAGE = 'はんてい'

/** ラウンドの初期値（rbxl: ReplicatedStorage/Round.Value = 1）。 */
const INITIAL_ROUND = 1

/** カウントダウンの開始値と刻み（Lua: for i = 1, 3 → 3,2,1 を wait(1) ずつ）。 */
const COUNTDOWN_START = 3
const COUNTDOWN_TICK_SECONDS = 1

/** せいこう！ の表示秒数（Lua: change() の wait(3)）。 */
const SUCCESS_DISPLAY_SECONDS = 3

/** せいこう！ の表示文言（SuccessTextLabel.Text の実測値）。 */
const SUCCESS_TEXT = 'せいこう！'

/** ラウンド表示の実機デザイン（RoundTextLabel の実測値。左上の緑バッジ）。 */
const ROUND_HUD_STYLE: HudStyle = {
  background: '#55FF7F',
  color: '#000000',
  left: 0,
}

/** カウントダウンの実機デザイン（CountTextLabel の実測値。黄色の帯）。 */
const COUNT_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#000000',
  borderWidth: 10,
  wide: true,
  top: '13%',
}

/** せいこう！ の実機デザイン（SuccessTextLabel の実測値。オレンジの帯）。 */
const SUCCESS_HUD_STYLE: HudStyle = {
  background: '#FFAA00',
  color: '#FFFFFF',
  border: '#FF0000',
  borderWidth: 10,
  wide: true,
  top: '13%',
}

export const createRjs08Behaviors = (): MissionBehaviors => {
  // Lua: ReplicatedStorage/Round.Value（サーバ側のラウンド数）。
  let round = INITIAL_ROUND
  // Lua: GUILocalScript の isCount（カウントダウンはラウンドごとに一度だけ）。
  let isCount = false
  // GUI の表示レイヤ状態（CountTextLabel / SuccessTextLabel の Visible 相当）。
  let countdownText: string | null = null
  let isSuccessVisible = false

  /**
   * HUD を現在の表示レイヤ状態から描き直す。実機は 3 枚の TextLabel が
   * 重なって出るが、プレビューは 1 チャンネルのため
   * せいこう！ > カウントダウン > ラウンド数 の優先で 1 枚に畳む。
   */
  const renderHud = (ctx: BehaviorContext): void => {
    if (isSuccessVisible) {
      ctx.setHudText?.(SUCCESS_TEXT, SUCCESS_HUD_STYLE)
    } else if (countdownText !== null) {
      ctx.setHudText?.(countdownText, COUNT_HUD_STYLE)
    } else {
      ctx.setHudText?.(String(round), ROUND_HUD_STYLE)
    }
  }

  /**
   * はんてい受信（Lua: GameManager の judge + GUILocalScript の countdown）。
   * 音は毎回鳴り、カウントダウンは isCount デバウンスで一度だけ動く。
   */
  const onJudge: BehaviorHandler = async (ctx) => {
    // Lua: judge() は isCount に関わらず毎回 Sting を鳴らす。
    playSample(JUMP_SFX.url, JUMP_SFX.volume, ctx.signal)
    if (isCount) {
      return
    }
    isCount = true
    // Lua: for i = 1, 3 do Text = 3 - i + 1; wait(1) end（3,2,1 を 1 秒ずつ）。
    for (let count = COUNTDOWN_START; count >= 1; count -= 1) {
      countdownText = String(count)
      renderHud(ctx)
      await ctx.wait(COUNTDOWN_TICK_SECONDS)
      if (ctx.signal.aborted) {
        return
      }
    }
    // Lua: CountTextLabel.Visible = false（isCount は立てたまま）。
    countdownText = null
    renderHud(ctx)
  }

  /**
   * せいこう受信（Lua: GameManager の nextRound + GUILocalScript の change）。
   * ラウンド +1 → Winner 音 → ラウンド表示更新 + せいこう！ を 3 秒表示 →
   * isCount 解除（次ラウンドのカウントダウンを許可）。
   */
  const onSuccess: BehaviorHandler = async (ctx) => {
    round += 1
    playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
    isSuccessVisible = true
    renderHud(ctx)
    await ctx.wait(SUCCESS_DISPLAY_SECONDS)
    if (ctx.signal.aborted) {
      return
    }
    // Lua と同じく共有フラグを下ろすだけ（多重受信時は先に終わった方で隠れる）。
    isSuccessVisible = false
    isCount = false
    renderHud(ctx)
  }

  /**
   * 死亡監視（Lua: PlayerDeathHandler）。体力 0 のエッジで毎回 Game Over 音を
   * 鳴らす（実機はリスポーンごとに Died を張り直すため、死亡のたびに鳴る）。
   */
  const watchDeath: BehaviorHandler = createDeathWatcher({
    once: false,
    onDeath: (ctx) => {
      playSample(FAIL_GAMEOVER_SFX.url, FAIL_GAMEOVER_SFX.volume, ctx.signal)
    },
  })

  /** 初期表示（Lua: GUILocalScript 末尾の RoundTextLabel.Text = round.Value）。 */
  const showInitialRound: BehaviorHandler = (ctx) => {
    renderHud(ctx)
  }

  /**
   * テンプレート（②ブロック）の隠蔽 + 学習者の control_clone 複製への
   * CanCollide/CanTouch 復元（詳細は primitives/restoreClonedTemplates.ts）。
   */
  const templateRestorer = createTemplateCloneRestorer(TEMPLATE_NAMES)
  const manageTemplates: BehaviorHandler = (ctx) => {
    templateRestorer.concealAndSnapshot(ctx.world)
    return templateRestorer.watchClones(ctx)
  }

  const onMessage = new Map<string, BehaviorHandler>()
  onMessage.set(JUDGE_MESSAGE, onJudge)
  onMessage.set(SUCCESS_MESSAGE, onSuccess)

  return {
    onPlay: [showInitialRound, manageTemplates, watchDeath],
    onMessage,
    // ②ブロック は実機では ServerStorage（Script 非実行）。本体では学習者
    // プログラムを起動しない（クローンにだけ起動する）。
    serverStorageTemplates: [...TEMPLATE_NAMES],
  }
}
